// frontend/Blog/BlogPage.jsx
// Public-facing blog listing — three view modes: Reading (default), Web, Double Page

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getPublishedBlogs, getFeaturedBlogs } from "../../services/publicblogapi";
import {
  Search, Clock, Eye, ArrowRight, Tag as TagIcon,
  BookOpen, ChevronLeft, ChevronRight, Loader2,
  TrendingUp, Sparkles, Filter, X,
  AlignLeft, LayoutGrid, BookCopy,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  "All","Education","Technology","Career","University",
  "Entrance Exams","Science","Arts & Humanities","Health","Business","Study Abroad",
];

const VIEW_MODES = [
  { id: "web",      Icon: LayoutGrid,  label: "Web" },
  { id: "double",   Icon: BookCopy,    label: "Book View" },
];

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
const categoryColor = (cat) => {
  const palette = {
    Education:"#4361ee", Technology:"#7c3aed", Career:"#f4803e",
    University:"#06b6d4","Entrance Exams":"#ef4444", Science:"#22c55e",
    "Arts & Humanities":"#ec4899", Health:"#16a34a", Business:"#f59e0b",
    "Study Abroad":"#8b5cf6",
  };
  return palette[cat] || "#6b7280";
};

// ─── Intersection observer hook ───────────────────────────────────────────────
function useReveal(delay = 0) {
  const ref  = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

// ─── Reading-mode row card ─────────────────────────────────────────────────────
function ReadingCard({ blog, index, onClick }) {
  const [ref, vis] = useReveal();
  const [hov, setHov] = useState(false);
  const color = categoryColor(blog.category);
  const delay = Math.min(index, 5) * 55;

  return (
    <article
      ref={ref}
      onClick={() => onClick(blog.slug || blog._id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", gap: "clamp(16px,3vw,28px)",
        alignItems: "flex-start",
        background: hov ? "#fdf8f4" : "#fff",
        borderRadius: "16px",
        padding: "clamp(16px,2.5vw,26px)",
        cursor: "pointer",
        border: `1.5px solid ${hov ? color + "30" : "#f0ede8"}`,
        boxShadow: hov ? `0 8px 32px ${color}18` : "0 2px 8px rgba(0,0,0,0.04)",
        transform: vis
          ? hov ? "translateX(4px)" : "translateX(0)"
          : "translateX(-18px)",
        opacity: vis ? 1 : 0,
        transition: `all 0.38s ease ${delay}ms`,
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: "clamp(80px,15vw,130px)", height: "clamp(72px,10vw,100px)",
        borderRadius: "12px", flexShrink: 0, overflow: "hidden",
        background: `linear-gradient(135deg,${color}18,${color}08)`,
        position: "relative",
      }}>
        {blog.featuredImg
          ? <img src={blog.featuredImg} alt={blog.title}
              style={{ width:"100%",height:"100%",objectFit:"cover",
                transform: hov ? "scale(1.06)" : "scale(1)",
                transition:"transform 0.4s ease" }} />
          : <div style={{ width:"100%",height:"100%",display:"flex",
              alignItems:"center",justifyContent:"center" }}>
              <BookOpen size={24} color={color} style={{ opacity:.35 }}/>
            </div>
        }
      </div>

      {/* Text */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px", flexWrap:"wrap" }}>
          <span style={{
            background: color, color:"#fff",
            padding:"2px 9px", borderRadius:"20px",
            fontSize:"10px", fontWeight:"800", letterSpacing:"0.4px",
            textTransform:"uppercase",
          }}>{blog.category}</span>
          {blog.tags?.slice(0,2).map(t => (
            <span key={t} style={{
              background:`${color}12`, color, fontSize:"10px",
              fontWeight:"600", padding:"2px 8px", borderRadius:"10px",
            }}>#{t}</span>
          ))}
        </div>

        <h3 style={{
          fontSize: "clamp(14px,1.6vw,17px)", fontWeight:"700",
          color: hov ? "#1a1a2e" : "#2d2d3f",
          fontFamily:"'Lora', serif",
          lineHeight:"1.38", marginBottom:"6px",
          display:"-webkit-box", WebkitLineClamp:2,
          WebkitBoxOrient:"vertical", overflow:"hidden",
        }}>{blog.title}</h3>

        {blog.excerpt && (
          <p style={{
            fontSize:"clamp(12px,1.2vw,13.5px)", color:"#8a8a9a",
            lineHeight:"1.65", marginBottom:"10px",
            display:"-webkit-box", WebkitLineClamp:2,
            WebkitBoxOrient:"vertical", overflow:"hidden",
          }}>{blog.excerpt}</p>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:"14px", flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"7px" }}>
            <div style={{
              width:"22px",height:"22px",borderRadius:"50%",flexShrink:0,
              background:`linear-gradient(135deg,${color},${color}99)`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:"10px",fontWeight:"800",color:"#fff",
            }}>{(blog.author||"A")[0].toUpperCase()}</div>
            <span style={{ fontSize:"11px",fontWeight:"600",color:"#6b6b80" }}>{blog.author||"Admin"}</span>
          </div>
          <span style={{ fontSize:"11px",color:"#b0b0c0" }}>{fmtDate(blog.publishedAt)}</span>
          <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11px",color:"#b0b0c0" }}>
            <Clock size={10}/> {blog.readTime||1} min
          </span>
          <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11px",color:"#b0b0c0" }}>
            <Eye size={10}/> {blog.views||0}
          </span>
          <div style={{ marginLeft:"auto" }}>
            <div style={{
              width:"28px",height:"28px",borderRadius:"50%",
              background: hov ? color : "#f0ede8",
              display:"flex",alignItems:"center",justifyContent:"center",
              transition:"background 0.2s",
            }}>
              <ArrowRight size={13} color={hov?"#fff":"#b0b0c0"}/>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// ─── Web-mode grid card ───────────────────────────────────────────────────────
function WebCard({ blog, index, onClick }) {
  const [ref, vis] = useReveal();
  const [hov, setHov] = useState(false);
  const color = categoryColor(blog.category);
  const delay = (index % 3) * 70;

  return (
    <div
      ref={ref}
      onClick={() => onClick(blog.slug || blog._id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:"#fff", borderRadius:"18px", overflow:"hidden",
        cursor:"pointer", display:"flex", flexDirection:"column",
        boxShadow: hov ? `0 22px 56px rgba(0,0,0,0.13),0 0 0 2px ${color}20` : "0 2px 14px rgba(0,0,0,0.06)",
        transform: vis
          ? hov ? "translateY(-6px) scale(1.01)" : "translateY(0)"
          : "translateY(28px)",
        opacity: vis ? 1 : 0,
        transition: `all 0.38s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms`,
        border:"1px solid #f0f0f8",
      }}
    >
      <div style={{ height:"192px",overflow:"hidden",background:`linear-gradient(135deg,${color}14,${color}06)`,flexShrink:0,position:"relative" }}>
        {blog.featuredImg
          ? <img src={blog.featuredImg} alt={blog.title}
              style={{ width:"100%",height:"100%",objectFit:"cover",
                transform:hov?"scale(1.07)":"scale(1)", transition:"transform 0.5s ease" }}/>
          : <div style={{ width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <BookOpen size={38} color={color} style={{ opacity:.3 }}/>
            </div>
        }
        <div style={{
          position:"absolute",top:"11px",left:"11px",
          background:color,color:"#fff",padding:"3px 10px",
          borderRadius:"20px",fontSize:"10px",fontWeight:"800",letterSpacing:"0.3px",
          textTransform:"uppercase",
        }}>{blog.category}</div>
      </div>

      <div style={{ padding:"18px 20px",flex:1,display:"flex",flexDirection:"column" }}>
        <h3 style={{
          fontSize:"15px",fontWeight:"700",color:"#1a1a2e",
          fontFamily:"'Lora',serif",lineHeight:"1.42",marginBottom:"9px",
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",
        }}>{blog.title}</h3>

        {blog.excerpt && (
          <p style={{
            fontSize:"12.5px",color:"#7b7b90",lineHeight:"1.65",marginBottom:"12px",
            display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",
            flex:1,
          }}>{blog.excerpt}</p>
        )}

        {blog.tags?.length > 0 && (
          <div style={{ display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"12px" }}>
            {blog.tags.slice(0,3).map(t=>(
              <span key={t} style={{
                background:`${color}10`,color,borderRadius:"10px",
                padding:"2px 7px",fontSize:"10px",fontWeight:"600",
              }}>#{t}</span>
            ))}
          </div>
        )}

        <div style={{
          display:"flex",alignItems:"center",justifyContent:"space-between",
          borderTop:"1px solid #f0f0f8",paddingTop:"11px",marginTop:"auto",
        }}>
          <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
            <div style={{
              width:"26px",height:"26px",borderRadius:"50%",flexShrink:0,
              background:`linear-gradient(135deg,${color},${color}aa)`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:"11px",fontWeight:"800",color:"#fff",
            }}>{(blog.author||"A")[0].toUpperCase()}</div>
            <div>
              <div style={{ fontSize:"11px",fontWeight:"600",color:"#3d3d50" }}>{blog.author||"Admin"}</div>
              <div style={{ fontSize:"9.5px",color:"#b0b0c0" }}>{fmtDate(blog.publishedAt)}</div>
            </div>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
            <span style={{ display:"flex",alignItems:"center",gap:"2px",fontSize:"10.5px",color:"#b0b0c0" }}>
              <Clock size={10}/>{blog.readTime||1}m
            </span>
            <div style={{
              width:"28px",height:"28px",borderRadius:"50%",
              background:hov?color:"#f4f4f8",
              display:"flex",alignItems:"center",justifyContent:"center",
              transition:"background 0.2s",
            }}>
              <ArrowRight size={13} color={hov?"#fff":"#b0b0c0"}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Double-page "book spread" card ──────────────────────────────────────────
function BookPage({ blog, side, onClick }) {
  const [hov, setHov] = useState(false);
  const color = blog ? categoryColor(blog.category) : "#e0ddd8";
  if (!blog) return (
    <div style={{
      flex:1,background:"#faf8f4",minHeight:"clamp(380px,55vw,560px)",
      borderRadius: side==="left" ? "12px 4px 4px 12px" : "4px 12px 12px 4px",
      display:"flex",alignItems:"center",justifyContent:"center",
    }}>
      <BookOpen size={36} color="#d4cfc8" style={{ opacity:.5 }}/>
    </div>
  );

  return (
    <div
      onClick={() => onClick(blog.slug || blog._id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex:1, minHeight:"clamp(380px,55vw,560px)",
        borderRadius: side==="left" ? "12px 0 0 12px" : "0 12px 12px 0",
        background:"#faf8f4",
        cursor:"pointer",
        display:"flex", flexDirection:"column",
        overflow:"hidden",
        boxShadow: hov
          ? side==="left"
            ? `-4px 0 32px rgba(0,0,0,0.1),0 8px 40px ${color}18`
            : `4px 0 32px rgba(0,0,0,0.1),0 8px 40px ${color}18`
          : "none",
        transform: hov
          ? side==="left" ? "scale(1.01) translateX(-2px)" : "scale(1.01) translateX(2px)"
          : "scale(1)",
        transition:"all 0.3s ease",
        position:"relative",
      }}
    >
      {/* Image */}
      <div style={{ height:"clamp(160px,26vw,260px)",overflow:"hidden",flexShrink:0,position:"relative" }}>
        {blog.featuredImg
          ? <img src={blog.featuredImg} alt={blog.title}
              style={{ width:"100%",height:"100%",objectFit:"cover",
                transform:hov?"scale(1.05)":"scale(1)",transition:"transform 0.5s ease" }}/>
          : <div style={{
              width:"100%",height:"100%",
              background:`linear-gradient(135deg,${color}22,${color}08)`,
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>
              <BookOpen size={44} color={color} style={{ opacity:.25 }}/>
            </div>
        }
        {/* Gradient overlay */}
        <div style={{
          position:"absolute",inset:0,
          background:"linear-gradient(180deg,transparent 50%,#faf8f4 100%)",
        }}/>
        {/* Category */}
        <div style={{
          position:"absolute",top:"12px",left:"12px",
          background:color,color:"#fff",
          padding:"3px 10px",borderRadius:"20px",
          fontSize:"10px",fontWeight:"800",letterSpacing:"0.4px",textTransform:"uppercase",
        }}>{blog.category}</div>
        {/* Page number decoration */}
        <div style={{
          position:"absolute",bottom:"10px",right:"14px",
          fontSize:"10px",color:"rgba(255,255,255,0.7)",fontStyle:"italic",
          fontFamily:"'Lora',serif",
        }}>{side==="left"?"◂ prev":"next ▸"}</div>
      </div>

      {/* Content */}
      <div style={{ padding:"clamp(16px,2.5vw,24px)",flex:1,display:"flex",flexDirection:"column" }}>
        {/* Decorative rule */}
        <div style={{
          height:"2px",
          background:`linear-gradient(90deg,${color},transparent)`,
          borderRadius:"2px",marginBottom:"14px",
        }}/>

        <h3 style={{
          fontSize:"clamp(16px,2vw,21px)",fontWeight:"700",
          color:"#1a1a2e",fontFamily:"'Lora',serif",
          lineHeight:"1.35",marginBottom:"10px",
          display:"-webkit-box",WebkitLineClamp:3,
          WebkitBoxOrient:"vertical",overflow:"hidden",
        }}>{blog.title}</h3>

        {blog.excerpt && (
          <p style={{
            fontSize:"clamp(12px,1.3vw,14px)",color:"#7a7a8e",
            lineHeight:"1.75",marginBottom:"16px",flex:1,
            display:"-webkit-box",WebkitLineClamp:4,
            WebkitBoxOrient:"vertical",overflow:"hidden",
            fontStyle:"italic",
          }}>{blog.excerpt}</p>
        )}

        {/* Tags */}
        {blog.tags?.length > 0 && (
          <div style={{ display:"flex",flexWrap:"wrap",gap:"5px",marginBottom:"14px" }}>
            {blog.tags.slice(0,3).map(t=>(
              <span key={t} style={{
                background:`${color}12`,color,fontSize:"10px",
                fontWeight:"600",padding:"3px 9px",borderRadius:"12px",
              }}>#{t}</span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{
          display:"flex",alignItems:"center",justifyContent:"space-between",
          paddingTop:"12px",borderTop:`1px solid ${color}18`,
          flexWrap:"wrap",gap:"8px",
        }}>
          <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
            <div style={{
              width:"28px",height:"28px",borderRadius:"50%",flexShrink:0,
              background:`linear-gradient(135deg,${color},${color}88)`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:"12px",fontWeight:"800",color:"#fff",
            }}>{(blog.author||"A")[0].toUpperCase()}</div>
            <div>
              <div style={{ fontSize:"11.5px",fontWeight:"700",color:"#3d3d50" }}>{blog.author||"Admin"}</div>
              <div style={{ fontSize:"10px",color:"#b0b0c0" }}>{fmtDate(blog.publishedAt)}</div>
            </div>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
            <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11px",color:"#b0b0c0" }}>
              <Clock size={10}/> {blog.readTime||1} min
            </span>
            <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11px",color:"#b0b0c0" }}>
              <Eye size={10}/> {blog.views||0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Featured banner ──────────────────────────────────────────────────────────
function FeaturedCard({ blog, onClick }) {
  const [hov, setHov] = useState(false);
  const color = categoryColor(blog.category);
  return (
    <div
      onClick={() => onClick(blog.slug || blog._id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:"#1a1a2e",borderRadius:"20px",overflow:"hidden",
        cursor:"pointer",position:"relative",
        minHeight:"clamp(220px,35vw,340px)",
        display:"flex",alignItems:"flex-end",
        transform:hov?"scale(1.01)":"scale(1)",
        boxShadow:hov?"0 24px 64px rgba(0,0,0,0.22)":"0 8px 32px rgba(0,0,0,0.14)",
        transition:"all 0.35s ease",
      }}
    >
      {blog.featuredImg && (
        <img src={blog.featuredImg} alt={blog.title} style={{
          position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",
          opacity:0.42,transform:hov?"scale(1.06)":"scale(1)",transition:"transform 0.5s ease",
        }}/>
      )}
      <div style={{
        position:"absolute",inset:0,
        background:"linear-gradient(180deg,transparent 18%,rgba(26,26,46,0.95) 85%)",
      }}/>
      <div style={{ position:"relative",padding:"clamp(18px,3vw,28px)",width:"100%" }}>
        <div style={{
          background:color,color:"#fff",padding:"3px 10px",borderRadius:"20px",
          fontSize:"10px",fontWeight:"800",display:"inline-block",marginBottom:"9px",
          textTransform:"uppercase",letterSpacing:"0.4px",
        }}>✦ Featured · {blog.category}</div>
        <h2 style={{
          fontSize:"clamp(15px,2.5vw,22px)",fontWeight:"700",color:"#fff",
          fontFamily:"'Lora',serif",lineHeight:"1.32",marginBottom:"10px",
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",
        }}>{blog.title}</h2>
        <div style={{ display:"flex",alignItems:"center",gap:"14px",flexWrap:"wrap" }}>
          <span style={{ fontSize:"11.5px",color:"rgba(255,255,255,0.68)" }}>By {blog.author||"Admin"}</span>
          <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11.5px",color:"rgba(255,255,255,0.55)" }}>
            <Clock size={10}/> {blog.readTime||1} min
          </span>
          <span style={{ display:"flex",alignItems:"center",gap:"3px",fontSize:"11.5px",color:"rgba(255,255,255,0.55)" }}>
            <Eye size={10}/> {blog.views||0}
          </span>
        </div>
      </div>
      <div style={{
        position:"absolute",top:"18px",right:"18px",
        width:"36px",height:"36px",borderRadius:"50%",
        background:"rgba(255,255,255,0.14)",backdropFilter:"blur(8px)",
        display:"flex",alignItems:"center",justifyContent:"center",
        transform:hov?"rotate(45deg)":"rotate(0deg)",transition:"transform 0.3s ease",
      }}>
        <ArrowRight size={15} color="#fff"/>
      </div>
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function Skeleton({ viewMode }) {
  if (viewMode === "web") return (
    <div style={{ display:"flex",flexDirection:"column",gap:"12px" }}>
      {[...Array(5)].map((_,i)=>(
        <div key={i} style={{
          display:"flex",gap:"20px",background:"#fff",borderRadius:"14px",
          padding:"20px",border:"1px solid #f0ede8",
        }}>
          <div style={{ width:"110px",height:"88px",borderRadius:"10px",background:"#f0ede8",flexShrink:0,animation:"shimmer 1.4s ease infinite" }}/>
          <div style={{ flex:1 }}>
            <div style={{ height:"12px",background:"#f0ede8",borderRadius:"6px",width:"40%",marginBottom:"10px",animation:"shimmer 1.4s ease infinite" }}/>
            <div style={{ height:"18px",background:"#f0ede8",borderRadius:"6px",width:"80%",marginBottom:"8px",animation:"shimmer 1.4s ease infinite" }}/>
            <div style={{ height:"14px",background:"#f0ede8",borderRadius:"6px",width:"60%",animation:"shimmer 1.4s ease infinite" }}/>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns: viewMode==="double"
        ? "repeat(auto-fill,minmax(min(100%,440px),1fr))"
        : "repeat(auto-fill,minmax(min(100%,300px),1fr))",
      gap:"18px",
    }}>
      {[...Array(6)].map((_,i)=>(
        <div key={i} style={{ background:"#fff",borderRadius:"16px",overflow:"hidden",border:"1px solid #f0f0f8" }}>
          <div style={{ height:"180px",background:"#f0f0f8",animation:"shimmer 1.4s ease infinite" }}/>
          <div style={{ padding:"18px" }}>
            <div style={{ height:"16px",background:"#f0f0f8",borderRadius:"6px",width:"70%",marginBottom:"10px",animation:"shimmer 1.4s ease infinite" }}/>
            <div style={{ height:"13px",background:"#f0f0f8",borderRadius:"6px",width:"90%",animation:"shimmer 1.4s ease infinite" }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main BlogPage ─────────────────────────────────────────────────────────────
export default function BlogPage() {
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("web"); // reading | web | double
  const [blogs, setBlogs]           = useState([]);
  const [featured, setFeatured]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory]     = useState("All");
  const [search, setSearch]         = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [bookSpread, setBookSpread] = useState(0); // which pair of articles (double mode)
  const [heroRef, heroVis]          = useReveal();
  const searchTimer                 = useRef(null);
  const LIMIT                       = viewMode === "double" ? 10 : 9;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublishedBlogs({
        page, limit: LIMIT,
        category: category !== "All" ? category : undefined,
        q: search || undefined,
      });
      setBlogs(res?.data || []);
      setTotalPages(Math.ceil((res?.total || 0) / LIMIT) || 1);
    } catch (e) {
      console.error("Failed to fetch blogs:", e);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, category, search, LIMIT]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getFeaturedBlogs()
      .then(res => setFeatured(res?.data || []))
      .catch(() => setFeatured([]));
  }, []);

  // Reset book spread when blogs change
  useEffect(() => { setBookSpread(0); }, [blogs]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSearchInput = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 450);
  };
  const handleCategoryChange = (cat) => { setCategory(cat); setPage(1); };
  const handleCardClick = (slug) => navigate(`/blog/${slug}`);

  // Double-page pairs
  const spreadPairs = [];
  for (let i = 0; i < blogs.length; i += 2) spreadPairs.push([blogs[i], blogs[i+1] || null]);
  const currentPair = spreadPairs[bookSpread] || [null, null];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      fontFamily:"'DM Sans',system-ui,sans-serif",
      background: "#f0f2f8",
      minHeight:"100svh", color:"#1a1a2e",
      transition:"background 0.4s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

        /* Navbar offset — matches Header h-16/h-20 */
        .blog-root { padding-top:64px; }
        @media(min-width:640px) { .blog-root { padding-top:80px; } }

        @keyframes fadeUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%,100%{opacity:.55} 50%{opacity:1} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes float   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes pageIn  { from{opacity:0;transform:translateX(30px)} to{opacity:1;transform:translateX(0)} }

        .fade-up { animation:fadeUp 0.5s ease both; }
        .spin { animation:spin 0.9s linear infinite; }

        /* Category chips */
        .cat-chip { transition:all 0.18s ease; }
        .cat-chip:hover { transform:translateY(-2px); }

        /* Mode switcher btn */
        .mode-btn { transition:all 0.2s ease; }
        .mode-btn:hover { opacity:.85; }

        /* Reading mode specifics */
        .reading-bg { background:#f5f2ec !important; }

        /* Double page book styles */
        .book-spine {
          width:3px; flex-shrink:0;
          background:linear-gradient(180deg,#c8c0b8,#a8a098,#c8c0b8);
          box-shadow:0 0 8px rgba(0,0,0,0.12);
          position:relative;
        }
        .book-spine::before {
          content:''; position:absolute; inset:0;
          background:linear-gradient(90deg,rgba(255,255,255,0.3),transparent,rgba(0,0,0,0.08));
        }

        /* Scrollbar */
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:#d1cdc8; border-radius:4px; }

        /* Responsive grid */
        .web-grid {
          display:grid;
          grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr));
          gap:18px;
        }
        @media(max-width:480px) { .web-grid { grid-template-columns:1fr 1fr; gap:12px; } }
        @media(max-width:360px) { .web-grid { grid-template-columns:1fr; } }

        /* Related grid responsive */
        .featured-grid {
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));
          gap:14px;
        }

        /* Double page mobile collapse */
        .book-spread {
          display:flex;
          gap:0;
        }
        @media(max-width:700px) {
          .book-spread { flex-direction:column; }
          .book-spine   { width:100%; height:3px; }
        }

        /* Search input placeholder */
        input::placeholder { color:rgba(255,255,255,0.42); }
        .reading-search input::placeholder { color:#b0a898; }

        /* Filter chip bar */
        .filter-bar {
          display:flex; flex-wrap:wrap; gap:8px;
          max-height:0; overflow:hidden;
          transition:max-height 0.38s cubic-bezier(0.4,0,0.2,1), margin 0.3s;
        }
        .filter-bar.open { max-height:160px; margin-bottom:14px; }
      `}</style>

      {/* ─── Navbar offset wrapper ─── */}
      <div className="blog-root">

        {/* ══ HERO ══════════════════════════════════════════════════════════ */}
        <div ref={heroRef} style={{
          background: viewMode==="reading"
            ? "linear-gradient(135deg,#2c2418 0%,#1a160f 60%,#0f0c08 100%)"
            : "linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)",
          padding:"clamp(32px,5vw,64px) clamp(16px,4vw,48px) clamp(24px,4vw,48px)",
          position:"relative", overflow:"hidden",
          transition:"background 0.5s ease",
        }}>
          {/* Blobs */}
          <div style={{ position:"absolute",top:"-60px",right:"-60px",width:"300px",height:"300px",borderRadius:"50%",background:"radial-gradient(circle,#f4803e20 0%,transparent 70%)",pointerEvents:"none" }}/>
          <div style={{ position:"absolute",bottom:"-40px",left:"5%",width:"220px",height:"220px",borderRadius:"50%",background:"radial-gradient(circle,#4361ee18 0%,transparent 70%)",pointerEvents:"none" }}/>

          <div style={{ maxWidth:"900px",margin:"0 auto",position:"relative" }}>
            {/* Label */}
            <div style={{
              display:"inline-flex",alignItems:"center",gap:"7px",
              background:"rgba(244,128,62,0.16)",border:"1px solid rgba(244,128,62,0.28)",
              borderRadius:"20px",padding:"5px 14px",marginBottom:"16px",
              opacity:heroVis?1:0,transform:heroVis?"none":"translateY(12px)",
              transition:"opacity 0.6s ease, transform 0.6s ease",
            }}>
              <Sparkles size={12} color="#f4803e"/>
              <span style={{ fontSize:"11px",fontWeight:"700",color:"#f4803e",letterSpacing:"0.5px" }}>
                Vidyazon Blog
              </span>
            </div>

            <h1 style={{
              fontSize:"clamp(24px,5vw,50px)",fontWeight:"700",color:"#fff",
              fontFamily:"'Lora',serif",lineHeight:"1.2",marginBottom:"12px",
              opacity:heroVis?1:0,transform:heroVis?"none":"translateY(16px)",
              transition:"opacity 0.6s ease 0.08s,transform 0.6s ease 0.08s",
            }}>
              Insights, Guides &amp;{" "}
              <span style={{ color:"#f4803e" }}>Expert Advice</span>
            </h1>
            <p style={{
              fontSize:"clamp(13px,1.7vw,16.5px)",color:"rgba(255,255,255,0.6)",
              lineHeight:"1.7",maxWidth:"520px",marginBottom:"24px",
              opacity:heroVis?1:0,transform:heroVis?"none":"translateY(16px)",
              transition:"opacity 0.6s ease 0.16s,transform 0.6s ease 0.16s",
            }}>
              Stay ahead with the latest in education, career planning, college admissions and more.
            </p>

            {/* Search + Mode Toggle row */}
            <div style={{
              display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap",
              opacity:heroVis?1:0,transform:heroVis?"none":"translateY(16px)",
              transition:"opacity 0.6s ease 0.24s,transform 0.6s ease 0.24s",
            }}>
              {/* Search */}
              <div style={{ flex:1,minWidth:"200px",maxWidth:"480px",position:"relative" }}>
                <Search size={15} color="#9ca3af" style={{ position:"absolute",left:"13px",top:"50%",transform:"translateY(-50%)",pointerEvents:"none" }}/>
                <input
                  value={searchInput}
                  onChange={e=>handleSearchInput(e.target.value)}
                  placeholder="Search articles, topics, authors…"
                  style={{
                    width:"100%",background:"rgba(255,255,255,0.11)",backdropFilter:"blur(12px)",
                    border:"1.5px solid rgba(255,255,255,0.16)",borderRadius:"10px",
                    padding:"11px 36px 11px 38px",color:"#fff",fontSize:"13.5px",
                    outline:"none",fontFamily:"inherit",boxSizing:"border-box",
                  }}
                />
                {searchInput && (
                  <button onClick={()=>{ setSearchInput(""); setSearch(""); setPage(1); }}
                    style={{ position:"absolute",right:"10px",top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",cursor:"pointer",color:"#9ca3af" }}>
                    <X size={14}/>
                  </button>
                )}
              </div>

              {/* View Mode Toggle */}
              <div style={{
                display:"flex",background:"rgba(255,255,255,0.1)",
                borderRadius:"10px",padding:"3px",gap:"2px",
                border:"1px solid rgba(255,255,255,0.14)",backdropFilter:"blur(8px)",
              }}>
                {VIEW_MODES.map(({ id, Icon, label }) => (
                  <button key={id} className="mode-btn"
                    onClick={() => { setViewMode(id); setBookSpread(0); }}
                    title={label}
                    style={{
                      display:"flex",alignItems:"center",gap:"5px",
                      background: viewMode===id ? "rgba(244,128,62,0.85)" : "transparent",
                      border:"none",borderRadius:"8px",
                      padding:"7px 10px",cursor:"pointer",
                      color: viewMode===id ? "#fff" : "rgba(255,255,255,0.6)",
                      fontSize:"11px",fontWeight:"700",
                      transition:"all 0.22s ease",
                      whiteSpace:"nowrap",
                    }}>
                    <Icon size={14}/>
                    <span style={{ display:"none" }} className="mode-label">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Mode label pill (shows current mode) ── */}
        <div style={{
          maxWidth:"1400px",margin:"0 auto",
          padding:"14px clamp(12px,3vw,32px) 0",
          display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap",
        }}>
          {VIEW_MODES.map(({ id, Icon, label }) => (
            <button key={id}
              onClick={() => { setViewMode(id); setBookSpread(0); }}
              style={{
                display:"flex",alignItems:"center",gap:"5px",
                background: viewMode===id ? "#f4803e" : "#fff",
                color: viewMode===id ? "#fff" : "#6b7280",
                border:`1.5px solid ${viewMode===id?"#f4803e":"#e5e7eb"}`,
                borderRadius:"20px",padding:"5px 14px",
                fontSize:"12px",fontWeight:"700",cursor:"pointer",
                boxShadow: viewMode===id ? "0 3px 10px #f4803e40" : "none",
                transition:"all 0.2s ease",
              }}>
              <Icon size={13}/>
              {label}
            </button>
          ))}
          <div style={{ marginLeft:"auto",fontSize:"12px",color:"#9ca3af",fontWeight:"500" }}>
           
            {viewMode==="web" && "🌐 Web Mode — card grid layout"}
            {viewMode==="double" && "📚 Book View — double-page spread"}
          </div>
        </div>

        {/* ══ FEATURED ══════════════════════════════════════════════════════ */}
        {featured.length > 0 && !search && category === "All" && (
          <div style={{ maxWidth:"1400px",margin:"0 auto",padding:"22px clamp(12px,3vw,32px) 0" }}>
            <div style={{ display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px" }}>
              <TrendingUp size={15} color="#f4803e"/>
              <span style={{ fontSize:"12px",fontWeight:"800",color:"#374151",textTransform:"uppercase",letterSpacing:"0.8px" }}>
                Trending Now
              </span>
            </div>
            <div className="featured-grid">
              {featured.map(b=>(
                <FeaturedCard key={b._id||b.id||b.slug} blog={b} onClick={handleCardClick}/>
              ))}
            </div>
          </div>
        )}

        {/* ══ FILTER BAR ════════════════════════════════════════════════════ */}
        <div style={{ maxWidth:"1400px",margin:"0 auto",padding:"20px clamp(12px,3vw,32px) 0" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"12px",flexWrap:"wrap",gap:"8px" }}>
            <div style={{ fontSize:"12px",fontWeight:"800",color:"#374151",textTransform:"uppercase",letterSpacing:"0.7px" }}>
              {search ? `Results for "${search}"` : category!=="All" ? category : "All Articles"}
            </div>
            <button
              onClick={()=>setShowFilter(f=>!f)}
              style={{
                display:"flex",alignItems:"center",gap:"5px",
                background:"#fff",border:"1.5px solid #e5e7eb",
                borderRadius:"8px",padding:"6px 13px",
                fontSize:"11.5px",fontWeight:"700",cursor:"pointer",color:"#374151",
                boxShadow:"0 1px 4px rgba(0,0,0,0.05)",
              }}>
              <Filter size={12}/> Filter{category!=="All"?` · ${category}`:""}
            </button>
          </div>

          <div className={`filter-bar${showFilter?" open":""}`}>
            {CATEGORIES.map(cat=>{
              const active = category===cat;
              const col = cat==="All"?"#1a1a2e":categoryColor(cat);
              return (
                <button key={cat} className="cat-chip"
                  onClick={()=>{ handleCategoryChange(cat); setShowFilter(false); }}
                  style={{
                    background:active?col:"#fff",
                    color:active?"#fff":"#374151",
                    border:`1.5px solid ${active?col:"#e5e7eb"}`,
                    borderRadius:"20px",padding:"6px 14px",
                    fontSize:"11.5px",fontWeight:"700",cursor:"pointer",
                    boxShadow:active?`0 3px 10px ${col}40`:"none",
                    transition:"all 0.2s",
                  }}>
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* ══ CONTENT AREA ══════════════════════════════════════════════════ */}
        <div style={{ maxWidth:"1400px",margin:"0 auto",padding:"16px clamp(12px,3vw,32px) clamp(48px,7vw,80px)" }}>

          {loading ? (
            <Skeleton viewMode={viewMode}/>
          ) : blogs.length===0 ? (
            <div style={{
              textAlign:"center",padding:"clamp(48px,8vw,80px) 20px",
              background:"#fff",borderRadius:"18px",
              boxShadow:"0 2px 12px rgba(0,0,0,0.05)",
              border:"1px solid #f0ede8",
            }}>
              <div style={{ fontSize:"44px",marginBottom:"14px",animation:"float 3s ease-in-out infinite" }}>📭</div>
              <h3 style={{ fontSize:"18px",fontWeight:"700",color:"#1a1a2e",marginBottom:"8px",fontFamily:"'Lora',serif" }}>No articles found</h3>
              <p style={{ fontSize:"13px",color:"#9a9aaa" }}>
                {search?`No results for "${search}"`:"No articles in this category yet."}
              </p>
              {(search||category!=="All") && (
                <button onClick={()=>{ setSearch(""); setSearchInput(""); setCategory("All"); setPage(1); }}
                  style={{
                    marginTop:"18px",background:"#f4803e",color:"#fff",border:"none",
                    borderRadius:"10px",padding:"11px 22px",cursor:"pointer",
                    fontSize:"13px",fontWeight:"700",boxShadow:"0 3px 10px #f4803e40",
                  }}>
                  Clear Filters
                </button>
              )}
            </div>

          ) : (
            <>
              {/* ── READING MODE ── */}
            
              {/* ── WEB MODE ── */}
              {viewMode==="web" && (
                <div className="web-grid">
                  {blogs.map((blog,i)=>(
                    <WebCard key={blog._id||blog.id||blog.slug} blog={blog} index={i} onClick={handleCardClick}/>
                  ))}
                </div>
              )}

              {/* ── DOUBLE PAGE (BOOK) MODE ── */}
              {viewMode==="double" && (
                <div>
                  {/* Book container */}
                  <div style={{
                    background:"#e8e4de",borderRadius:"20px",
                    padding:"clamp(16px,3vw,28px)",
                    boxShadow:"0 16px 64px rgba(0,0,0,0.14),0 0 0 1px rgba(0,0,0,0.06)",
                    maxWidth:"1100px",margin:"0 auto",
                  }}>
                    {/* Book top strip */}
                    <div style={{
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                      marginBottom:"16px",flexWrap:"wrap",gap:"10px",
                    }}>
                      <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
                        <BookCopy size={15} color="#8a7a6a"/>
                        <span style={{ fontSize:"11px",fontWeight:"700",color:"#8a7a6a",textTransform:"uppercase",letterSpacing:"0.7px" }}>
                          Book View — Spread {bookSpread+1} of {spreadPairs.length}
                        </span>
                      </div>
                      {/* Page navigation */}
                      <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
                        <button
                          onClick={()=>setBookSpread(s=>Math.max(0,s-1))}
                          disabled={bookSpread===0}
                          style={{
                            width:"32px",height:"32px",borderRadius:"50%",
                            background:bookSpread===0?"#d8d4ce":"#1a1a2e",
                            border:"none",cursor:bookSpread===0?"not-allowed":"pointer",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            transition:"background 0.2s",
                          }}>
                          <ChevronLeft size={15} color={bookSpread===0?"#a09888":"#fff"}/>
                        </button>
                        <div style={{ display:"flex",gap:"4px" }}>
                          {spreadPairs.map((_,i)=>(
                            <button key={i}
                              onClick={()=>setBookSpread(i)}
                              style={{
                                width: i===bookSpread?"20px":"8px",
                                height:"8px",borderRadius:"8px",border:"none",cursor:"pointer",
                                background: i===bookSpread?"#f4803e":"#c8c0b8",
                                transition:"all 0.3s ease",
                              }}/>
                          ))}
                        </div>
                        <button
                          onClick={()=>setBookSpread(s=>Math.min(spreadPairs.length-1,s+1))}
                          disabled={bookSpread===spreadPairs.length-1}
                          style={{
                            width:"32px",height:"32px",borderRadius:"50%",
                            background:bookSpread===spreadPairs.length-1?"#d8d4ce":"#1a1a2e",
                            border:"none",cursor:bookSpread===spreadPairs.length-1?"not-allowed":"pointer",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            transition:"background 0.2s",
                          }}>
                          <ChevronRight size={15} color={bookSpread===spreadPairs.length-1?"#a09888":"#fff"}/>
                        </button>
                      </div>
                    </div>

                    {/* Pages */}
                    <div className="book-spread" style={{ animation:"pageIn 0.3s ease" }}>
                      <BookPage blog={currentPair[0]} side="left" onClick={handleCardClick}/>
                      <div className="book-spine"/>
                      <BookPage blog={currentPair[1]} side="right" onClick={handleCardClick}/>
                    </div>

                    {/* Bottom: all article thumbnails in this fetch */}
                    <div style={{
                      marginTop:"18px",paddingTop:"14px",
                      borderTop:"1px solid #d4cfc8",
                      display:"flex",flexWrap:"wrap",gap:"8px",justifyContent:"center",
                    }}>
                      {blogs.map((b,i)=>{
                        const pairIdx = Math.floor(i/2);
                        const active  = pairIdx===bookSpread;
                        const col     = categoryColor(b.category);
                        return (
                          <button key={b._id||b.id||b.slug}
                            onClick={()=>setBookSpread(pairIdx)}
                            style={{
                              width:"36px",height:"36px",borderRadius:"8px",overflow:"hidden",
                              border:`2px solid ${active?col:"transparent"}`,
                              cursor:"pointer",padding:0,background:"#d4cfc8",
                              boxShadow:active?`0 0 0 2px ${col}40`:"none",
                              transition:"all 0.2s",flexShrink:0,
                            }}>
                            {b.featuredImg
                              ? <img src={b.featuredImg} alt=""
                                  style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                              : <div style={{ width:"100%",height:"100%",background:`${col}20`,display:"flex",alignItems:"center",justifyContent:"center" }}>
                                  <BookOpen size={14} color={col}/>
                                </div>
                            }
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Pagination (reading + web modes) ── */}
              {viewMode!=="double" && totalPages>1 && (
                <div style={{
                  display:"flex",alignItems:"center",justifyContent:"center",
                  gap:"7px",marginTop:"40px",flexWrap:"wrap",
                }}>
                  <button
                    onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                    style={{
                      background:"#fff",border:"1.5px solid #e5e7eb",borderRadius:"9px",
                      padding:"8px 14px",cursor:page===1?"not-allowed":"pointer",
                      display:"flex",alignItems:"center",gap:"4px",
                      fontSize:"12.5px",fontWeight:"700",
                      color:page===1?"#b0b0c0":"#374151",
                    }}>
                    <ChevronLeft size={14}/> Prev
                  </button>

                  {Array.from({ length:Math.min(totalPages,7) },(_,i)=>{
                    const p = totalPages<=7 ? i+1 : i===0?1 : i===6?totalPages : page-2+i;
                    return (
                      <button key={p} onClick={()=>setPage(p)}
                        style={{
                          width:"34px",height:"34px",borderRadius:"8px",border:"none",
                          background:p===page?"#f4803e":"#fff",
                          color:p===page?"#fff":"#374151",
                          border:`1.5px solid ${p===page?"#f4803e":"#e5e7eb"}`,
                          cursor:"pointer",fontSize:"12.5px",fontWeight:p===page?"800":"500",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          boxShadow:p===page?"0 2px 8px #f4803e40":"none",
                        }}>
                        {p}
                      </button>
                    );
                  })}

                  <button
                    onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                    style={{
                      background:"#fff",border:"1.5px solid #e5e7eb",borderRadius:"9px",
                      padding:"8px 14px",cursor:page===totalPages?"not-allowed":"pointer",
                      display:"flex",alignItems:"center",gap:"4px",
                      fontSize:"12.5px",fontWeight:"700",
                      color:page===totalPages?"#b0b0c0":"#374151",
                    }}>
                    Next <ChevronRight size={14}/>
                  </button>
                </div>
              )}

              {/* ── Double-page pagination (full pages) ── */}
              {viewMode==="double" && page<totalPages && (
                <div style={{ textAlign:"center",marginTop:"24px" }}>
                  <button
                    onClick={()=>{ setPage(p=>p+1); setBookSpread(0); }}
                    style={{
                      background:"linear-gradient(135deg,#1a1a2e,#0f3460)",
                      color:"#fff",border:"none",borderRadius:"12px",
                      padding:"12px 28px",cursor:"pointer",
                      fontSize:"13px",fontWeight:"700",
                      boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
                      display:"inline-flex",alignItems:"center",gap:"7px",
                    }}>
                    <BookOpen size={14}/> Load More Articles
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}